import dayjs from 'dayjs';

import type {
  ChannelCourseResource,
  ChannelExpandedCommon,
} from '../../../api/channel';
import type {PostApiModel} from '../../../api/channel/post';
import type {Nullable} from '../../utility-types';

import {membershipStore} from '../../../api/membership';
import {currentUserStore} from '../../../api/user';
import {datetimeObjToISOString, type Timestamp} from '../../datetime';
import {isCourseCorporate} from '../course';
import {isMeCoachForEntity} from '../isMeCoachForEntity';

type StrategyParams = {
  channel: ChannelExpandedCommon;
  isArchived: boolean;
  isMeCourseCoach: boolean;
  lastPost?: Nullable<PostApiModel>;
};

// Local-time windows are scoped to their partner group, never shared across groups.
const ARCHIVED_CLIENT_PING_RULES = [
  {
    endsBefore: '2030-02-06T23:59:59',
    // Amazon users + impact (prod). YC-2742 uses 2030 for "no end date".
    partnerIds: ['6', '6#', '6##'],
    startInclusive: false,
    startsAt: '2026-01-29T00:00:00',
  },
  {
    endsBefore: '2026-09-19T00:00:00',
    // YC-2969: include all of September 9–18, 2026.
    partnerIds: ['metlife_pilot'],
    startInclusive: true,
    startsAt: '2026-09-09T00:00:00',
  },
];

export const isChannelReadonly = (
  channel: ChannelExpandedCommon,
  lastPost?: Nullable<PostApiModel>,
): boolean => {
  const isCourseChannel = channel.type === 'course';

  if (!isCourseChannel) {
    return false;
  }

  const isArchived = channel.status === 'archived';
  const isMeCourseCoach = isMeCoachForEntity(channel.resource);

  let isCorporate = false;

  if (isCourseChannel) {
    const course = channel.resource as ChannelCourseResource;

    isCorporate = isCourseCorporate(course);
  }

  const params: StrategyParams = {
    channel,
    isArchived,
    isMeCourseCoach,
    lastPost,
  };

  return isCorporate ? b2bStrategy(params) : coreStrategy(params);
};

export function checkIsCoachAllowedToPing({
  clientId,
  courseEndDate,
  partnerId,
}: {
  clientId: Nullable<string> | undefined;
  courseEndDate: Timestamp;
  partnerId: Nullable<string> | undefined;
}): boolean {
  /**
   * Reason: we want coaches to try to bring archived clients back to us
   * or notify them about something important.
   */

  if (!partnerId) {
    return false;
  }

  const now = dayjs();

  const isPingAllowedByRule = ARCHIVED_CLIENT_PING_RULES.some(rule => {
    if (!rule.partnerIds.includes(partnerId)) {
      return false;
    }

    const startsAt = dayjs(rule.startsAt);
    const hasStarted =
      now.isAfter(startsAt) || (rule.startInclusive && now.isSame(startsAt));

    return hasStarted && now.isBefore(rule.endsBefore);
  });

  if (!isPingAllowedByRule) {
    return false;
  }

  const isPingAllowedByEndDate = dayjs(courseEndDate).isAfter(
    /**
     * as part of YC-2742 we found out
     * that restriction "course end date after 01.12.2023" is redundant.
     * Manual test team define it as buggy behaviour.
     * So that's why we set end of 2018.
     */
    dayjs('2018-12-01T00:00:00'),
  );

  const isPingAllowedByClient = clientId
    ? !EXCLUDED_CLIENT_IDS.includes(clientId)
    : true;

  return isPingAllowedByEndDate && isPingAllowedByClient;
}

/**
 * On b2b side we should allow coaches post a message
 * only if the client messaged them first after the course has been archived.
 *
 * Also, we give them only 30 days to post a message after the course end date.
 * It's enough to allow them to arrange a course extension.
 *
 * IMPORTANT: We need to keep it in sync with ChannelModel::isReadonly
 * @see https://github.com/yourcoach/frontend-shared/blob/master/modules/chats/channel/models/Channel/ChannelModel/Channel.model.ts#L428
 */
function b2bStrategy(params: StrategyParams) {
  const {channel, isArchived, isMeCourseCoach, lastPost} = params;

  if (!isArchived) {
    return false;
  }

  const course = channel.resource as ChannelCourseResource | undefined;

  const courseEndDate = course?.end_date
    ? dayjs(datetimeObjToISOString(course.end_date))
    : dayjs('2020-01-01T00:00:00');

  // for now it's enough to check only individual courses
  const clientId = course?.client_id;

  if (
    checkIsCoachAllowedToPing({
      clientId,
      courseEndDate: courseEndDate.valueOf(),
      partnerId: course?.subtenant?.partner_id,
    })
  ) {
    return false;
  }

  if (dayjs().diff(courseEndDate, 'days') > 30) {
    return true;
  }

  if (!lastPost) {
    // allow to post only clients if there are no posts
    return isMeCourseCoach;
  }

  const isLastPostMine = lastPost.user_id === currentUserStore.user?._id;

  // should wait for a client post first
  if (isMeCourseCoach && isLastPostMine) {
    return true;
  }

  return false;
}

/**
 * Core is simple. We allow to post only if the course is not archived and
 * the user is not frozen.
 */
function coreStrategy(params: StrategyParams) {
  const {channel, isArchived, isMeCourseCoach} = params;

  const clientMembership = channel.resource_id
    ? membershipStore.membershipLookup.get(channel.resource_id)
    : undefined;

  let isUserFrozen = false;

  if (!isMeCourseCoach && clientMembership) {
    isUserFrozen = clientMembership.status === 'frozen';
  }

  return isUserFrozen || isArchived;
}

const EXCLUDED_CLIENT_IDS = [
  // PROD B2B backend
  'user:63ed891e8a03a16e46734e23',
  'user:67df0aff15cfd92920299cdd',
  'user:6883ff1549110602a244881e',
  'user:67d48e302301eb04c859de9b',
  'user:65c17c864d444775ad1d87da',
  'user:638e9d6baa53a807adf9db8c',
  'user:66b8c5a9072d7d56295df329',
  'user:67d0e1f9570131cc469cc12f',
  'user:67eea882b3706d29f1eb3604',
  'user:64a09930771256698eb1a1a2',
  'user:650862fdd297ce1aaaa3c380',
  'user:68241715be58595bd5879e7c',
  'user:67e364f1357d928ef52c3ef1',
  'user:635974efb883f1c625f46b34',
  'user:664d30b3689dec8a49da621d',
  'user:63c9652d62a6d402b7150def',
  'user:67d9cecf15cfd92920286bb9',
  'user:67e1d13f15cfd9292029f995',
  'user:67df8f3a15cfd9292029a464',
  'user:67dde8a115cfd92920298c45',
  'user:68631c3afe93eb8197744bec',
  'user:648875af676309c280ffe658',
  'user:6736968ddcc5134acabcdfdf',
  'user:66a41cbb4d84278bd423c16c',
  'user:67368216dcc5134acabcd06e',
  'user:6337612b0d45854e7c6ef972',
  'user:63376c580d45854e7c6efecd',
  'user:664f97f45e6d48f113ee8346',
  'user:67dc618915cfd929202922c8',
  'user:66abbb754d84278bd424eefb',
  'user:67db2edb15cfd9292028df0a',
  'user:680800585bc466edf66d1f13',
  'user:67db46ca15cfd9292028e6c8',
  'user:67d2b89e570131cc469d0d55',
  'user:66007bfbdd3101a4515bfc7f',
  'user:655ac08d3ae1ec634193a0e4',
  'user:67d1bea3570131cc469ce559',
  'user:674f23a2bb58f6cbb0bf67f0',
  'user:67e44184357d928ef52c6d44',
  'user:67d1286f570131cc469cc746',
  'user:6810f70d37083260e64d800e',
  'user:6513bb61684ec3e6df1fb4db',
  'user:67d67bdd2301eb04c85a79ad',
  'user:67d1c638570131cc469ce930',
  'user:67d382db2301eb04c85969de',
  'user:67e2c21d357d928ef52c08e2',
  'user:648a676d676309c28000a7a6',
  'user:670c597d4c683713146b29cc',
  'user:68631938fe93eb8197744ac8',
  'user:6851abf903d0f2f819175492',
  'user:633652d50d45854e7c6ea3d6',
  'user:67d345972301eb04c8594c9c',
  'user:63f3ac5aca9dd75a7a7dab1c',
  'user:666f4f1c7da41a7f5442a360',
  'user:68581e6b03d0f2f81917f15a',
  'user:67d070ff570131cc469c9f12',
  'user:655558d16f843882c339de79',
  'user:67fe7034ca1577f0e7527808',
  'user:67e2237a15cfd929202a06b8',
  'user:66d07b84125859cc64748337',
  'user:6830eddc65cabea3552956a4',
  'user:68176745950450579337e180',
  'user:67708d183e37be1952b257b7',
  'user:667d5c6f103eb40d9d738122',
  'user:67dca6d615cfd92920294158',
  'user:6761dc8e3e37be1952af9ba9',
  'user:67cf27350a3a2a298755b9f9',
  'user:67d0851f570131cc469ca6cb',
  'user:639fb30f4c4017bfdf818ba5',
  'user:67cb6ef7deee0753fe7b7378',
  'user:678eb4cd389553f8446ec843',
  'user:68caf88936f06b59711f8129',
  'user:68910d06d94acf0fd6618276',
  'user:6890456908dcd16d042440ae',
  'user:688b883fa41f5ff266d23889',
  'user:686382bbfe93eb8197745411',
  'user:6864f03df310de57fe3aeb61',
  'user:686724d6ed2f882f20028ddc',
  'user:686bf83cfbf5dba1de2273ca',
  'user:686d5497fbf5dba1de22a5ab',
  'user:68718c483814700b2e558d6d',
  'user:687e14fd550a582b135a92f8',
  'user:68807d39550a582b135ae486',
  'user:6880f50f550a582b135aee73',
  'user:6957f5ca491366a770ae69ae',
  'user:6959f41f491366a770ae905b',

  // RC B2B backend
  'user:688b8a2fa660dce3d87b1331',
  'user:688b831ba660dce3d87b12fd',
  'user:686e0cfa0a478783c1dc5d53',
  'user:68078eeb7d25e216b0135665',
];
